/**
 * Update pagination styling
 */

import { Linkable } from '@/components/blocks/Linkable';
import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import { pagination } from '@/components/content/CatalogEntryList/styles/pagination';
import { HDPagination } from '@/components/content/HdmPagination';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import {
	Box,
	PaginationItem,
	PaginationRenderItemParams,
	SxProps,
	Theme,
	Typography,
	useTheme,
} from '@mui/material';
import { useCallback, useContext, useMemo } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

const EMPTY_OBJECT = {};

export const CatalogEntryListPagination: React.FC = () => {
	const {
		pageCount,
		pageNumber,
		getPageLink,
		total = 0,
		filteredParams,
	} = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const localization = useLocalization('Pagination');
	const theme = useTheme();

	const PrevIcon = () => (
		<DynamicIcon name={MATERIAL_SYMBOL_ICONS.chevronLeft} color={theme.palette.secondary.main} />
	);

	const NextIcon = () => (
		<DynamicIcon name={MATERIAL_SYMBOL_ICONS.chevronRight} color={theme.palette.secondary.main} />
	);

	const renderItem = useCallback(
		(item: PaginationRenderItemParams, sx: SxProps<Theme>) => {
			const { page, type } = item;
			let id = null;
			let className = null;
			if (type === 'previous') {
				id = 'previousPageButton';
			}

			if (type === 'next') {
				id = 'nextPageButton';
			}

			if (type === 'page') {
				className = 'pagination-button';
			}

			const href = page !== null ? { href: getPageLink(page) } : EMPTY_OBJECT;
			return (
				<PaginationItem
					component={Linkable}
					{...href}
					{...item}
					{...(id && { id })}
					{...(className && { className })}
					sx={sx}
					slots={{ previous: PrevIcon, next: NextIcon }}
				/>
			);
		},
		[getPageLink]
	);

	const paginationLegendParams = useMemo(() => {
		const to =
			filteredParams?.offset + filteredParams?.limit > total
				? total
				: filteredParams?.offset + filteredParams?.limit;

		return {
			from: filteredParams?.offset + 1,
			to,
			totalItems: total,
		};
	}, [filteredParams.offset, filteredParams.limit, total]);

	return pageCount && pageCount > 1 ? (
		<Box sx={{ mt: '5px' }}>
			<HDPagination
				currentPage={pageNumber}
				numberOfPages={pageCount}
				renderItem={renderItem}
				idContainer="paginationCard"
				customIcons
			/>
			<Box sx={{ my: '20px' }} display="flex" justifyContent="center">
				<Typography component="p" variant="bodyBaseline" sx={pagination.paginationLegend}>
					<LocalizationWithComponent
						text={localization.paginationLegend.t({ ...paginationLegendParams })}
						components={[<b key="b" />]}
					/>
				</Typography>
			</Box>
		</Box>
	) : null;
};
